'use client';

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import { createTheme } from '@ant-design/cssinjs';
import IconContext from "@ant-design/icons/es/components/Context";
import useMemo from "rc-util/es/hooks/useMemo";
import { merge } from "rc-util/es/utils/set";
import * as React from 'react';
import warning from '../_util/warning';
import ValidateMessagesContext from '../form/validateMessagesContext';
import LocaleProvider, { ANT_MARK } from '../locale';
import LocaleContext from '../locale/context';
import defaultLocale from '../locale/en_US';
import { DesignTokenContext } from '../theme/internal';
import defaultSeedToken from '../theme/themes/seed';
import { ConfigConsumer, ConfigContext, defaultIconPrefixCls } from './context';
import { registerTheme } from './cssVariables';
import { DisabledContextProvider } from './DisabledContext';
import useConfig from './hooks/useConfig';
import useTheme from './hooks/useTheme';
import MotionWrapper from './MotionWrapper';
import SizeContext, { SizeContextProvider } from './SizeContext';
import useStyle from './style';
import { defaultTheme } from '../theme/context';
/**
 * Since too many feedback using static method like `Modal.confirm` not getting theme, we record the
 * theme register info here to help developer get warning info.
 */

var existThemeConfig = false;
export var warnContext = process.env.NODE_ENV !== 'production' ? function (componentName) {
  process.env.NODE_ENV !== "production" ? warning(!existThemeConfig, componentName, "Static function can not consume context like dynamic theme. Please use 'App' component instead.") : void 0;
} :
/* istanbul ignore next */
null;
export { ConfigConsumer, ConfigContext, defaultIconPrefixCls };
export var configConsumerProps = ['getTargetContainer', 'getPopupContainer', 'rootPrefixCls', 'getPrefixCls', 'renderEmpty', 'csp', 'autoInsertSpaceInButton', 'locale', 'pageHeader']; // These props is used by `useContext` directly in sub component

var PASSED_PROPS = ['getTargetContainer', 'getPopupContainer', 'renderEmpty', 'pageHeader', 'input', 'pagination', 'form', 'select', 'button'];
export var defaultPrefixCls = 'ant';
var globalPrefixCls;
var globalIconPrefixCls;
var globalTheme;

function getGlobalPrefixCls() {
  return globalPrefixCls || defaultPrefixCls;
}

function getGlobalIconPrefixCls() {
  return globalIconPrefixCls || defaultIconPrefixCls;
}

function isLegacyTheme(theme) {
  return Object.keys(theme).some(function (key) {
    return key.endsWith('Color');
  });
}

var setGlobalConfig = function setGlobalConfig(_ref) {
  var prefixCls = _ref.prefixCls,
      iconPrefixCls = _ref.iconPrefixCls,
      theme = _ref.theme;

  if (prefixCls !== undefined) {
    globalPrefixCls = prefixCls;
  }

  if (iconPrefixCls !== undefined) {
    globalIconPrefixCls = iconPrefixCls;
  }

  if (theme) {
    if (isLegacyTheme(theme)) {
      process.env.NODE_ENV !== "production" ? warning(false, 'ConfigProvider', '`config` of css variable theme is not work in v5. Please use new `theme` config instead.') : void 0;
      registerTheme(getGlobalPrefixCls(), theme);
    } else {
      globalTheme = theme;
    }
  }
};

export var globalConfig = function globalConfig() {
  return {
    getPrefixCls: function getPrefixCls(suffixCls, customizePrefixCls) {
      if (customizePrefixCls) {
        return customizePrefixCls;
      }

      return suffixCls ? "".concat(getGlobalPrefixCls(), "-").concat(suffixCls) : getGlobalPrefixCls();
    },
    getIconPrefixCls: getGlobalIconPrefixCls,
    getRootPrefixCls: function getRootPrefixCls() {
      // If Global prefixCls provided, use this
      if (globalPrefixCls) {
        return globalPrefixCls;
      } // Fallback to default prefixCls


      return getGlobalPrefixCls();
    },
    getTheme: function getTheme() {
      return globalTheme;
    }
  };
};

var ProviderChildren = function ProviderChildren(props) {
  var children = props.children,
      customCsp = props.csp,
      autoInsertSpaceInButton = props.autoInsertSpaceInButton,
      alert = props.alert,
      anchor = props.anchor,
      form = props.form,
      locale = props.locale,
      componentSize = props.componentSize,
      direction = props.direction,
      space = props.space,
      virtual = props.virtual,
      dropdownMatchSelectWidth = props.dropdownMatchSelectWidth,
      popupMatchSelectWidth = props.popupMatchSelectWidth,
      popupOverflow = props.popupOverflow,
      legacyLocale = props.legacyLocale,
      parentContext = props.parentContext,
      customIconPrefixCls = props.iconPrefixCls,
      theme = props.theme,
      componentDisabled = props.componentDisabled,
      segmented = props.segmented,
      statistic = props.statistic,
      spin = props.spin,
      calendar = props.calendar,
      carousel = props.carousel,
      cascader = props.cascader,
      collapse = props.collapse,
      typography = props.typography,
      checkbox = props.checkbox,
      descriptions = props.descriptions,
      divider = props.divider,
      drawer = props.drawer,
      skeleton = props.skeleton,
      steps = props.steps,
      image = props.image,
      layout = props.layout,
      list = props.list,
      mentions = props.mentions,
      modal = props.modal,
      progress = props.progress,
      result = props.result,
      slider = props.slider,
      breadcrumb = props.breadcrumb,
      menu = props.menu,
      pagination = props.pagination,
      input = props.input,
      empty = props.empty,
      badge = props.badge,
      radio = props.radio,
      rate = props.rate,
      SWITCH = props.switch,
      transfer = props.transfer,
      avatar = props.avatar,
      message = props.message,
      tag = props.tag,
      table = props.table,
      card = props.card,
      tabs = props.tabs,
      timeline = props.timeline,
      timePicker = props.timePicker,
      upload = props.upload,
      notification = props.notification,
      tree = props.tree,
      colorPicker = props.colorPicker,
      datePicker = props.datePicker,
      wave = props.wave; // =================================== Warning ===================================

  if (process.env.NODE_ENV !== 'production') {
    process.env.NODE_ENV !== "production" ? warning(dropdownMatchSelectWidth === undefined, 'ConfigProvider', '`dropdownMatchSelectWidth` is deprecated. Please use `popupMatchSelectWidth` instead.') : void 0;
  } // =================================== Context ===================================


  var getPrefixCls = React.useCallback(function (suffixCls, customizePrefixCls) {
    var prefixCls = props.prefixCls;

    if (customizePrefixCls) {
      return customizePrefixCls;
    }

    var mergedPrefixCls = prefixCls || parentContext.getPrefixCls('');
    return suffixCls ? "".concat(mergedPrefixCls, "-").concat(suffixCls) : mergedPrefixCls;
  }, [parentContext.getPrefixCls, props.prefixCls]);
  var iconPrefixCls = customIconPrefixCls || parentContext.iconPrefixCls || defaultIconPrefixCls;
  var shouldWrapSSR = iconPrefixCls !== parentContext.iconPrefixCls;
  var csp = customCsp || parentContext.csp;
  var wrapSSR = useStyle(iconPrefixCls, csp);
  var mergedTheme = useTheme(theme, parentContext.theme);

  if (process.env.NODE_ENV !== 'production') {
    existThemeConfig = existThemeConfig || !!mergedTheme;
  }

  var baseConfig = {
    csp: csp,
    autoInsertSpaceInButton: autoInsertSpaceInButton,
    alert: alert,
    anchor: anchor,
    locale: locale || legacyLocale,
    direction: direction,
    space: space,
    virtual: virtual,
    popupMatchSelectWidth: popupMatchSelectWidth !== null && popupMatchSelectWidth !== void 0 ? popupMatchSelectWidth : dropdownMatchSelectWidth,
    popupOverflow: popupOverflow,
    getPrefixCls: getPrefixCls,
    iconPrefixCls: iconPrefixCls,
    theme: mergedTheme,
    segmented: segmented,
    statistic: statistic,
    spin: spin,
    calendar: calendar,
    carousel: carousel,
    cascader: cascader,
    collapse: collapse,
    typography: typography,
    checkbox: checkbox,
    descriptions: descriptions,
    divider: divider,
    drawer: drawer,
    skeleton: skeleton,
    steps: steps,
    image: image,
    input: input,
    layout: layout,
    list: list,
    mentions: mentions,
    modal: modal,
    progress: progress,
    result: result,
    slider: slider,
    breadcrumb: breadcrumb,
    menu: menu,
    pagination: pagination,
    empty: empty,
    badge: badge,
    radio: radio,
    rate: rate,
    switch: SWITCH,
    transfer: transfer,
    avatar: avatar,
    message: message,
    tag: tag,
    table: table,
    card: card,
    tabs: tabs,
    timeline: timeline,
    timePicker: timePicker,
    upload: upload,
    notification: notification,
    tree: tree,
    colorPicker: colorPicker,
    datePicker: datePicker,
    wave: wave
  };
  var config = Object.assign({}, parentContext);
  Object.keys(baseConfig).forEach(function (key) {
    if (baseConfig[key] !== undefined) {
      config[key] = baseConfig[key];
    }
  }); // Pass the props used by `useContext` directly with child component.
  // These props should merged into `config`.

  PASSED_PROPS.forEach(function (propName) {
    var propValue = props[propName];

    if (propValue) {
      config[propName] = propValue;
    }
  }); // https://github.com/ant-design/ant-design/issues/27617

  var memoedConfig = useMemo(function () {
    return config;
  }, config, function (prevConfig, currentConfig) {
    var prevKeys = Object.keys(prevConfig);
    var currentKeys = Object.keys(currentConfig);
    return prevKeys.length !== currentKeys.length || prevKeys.some(function (key) {
      return prevConfig[key] !== currentConfig[key];
    });
  });
  var memoIconContextValue = React.useMemo(function () {
    return {
      prefixCls: iconPrefixCls,
      csp: csp
    };
  }, [iconPrefixCls, csp]);
  var childNode = shouldWrapSSR ? wrapSSR(children) : children;
  var validateMessages = React.useMemo(function () {
    var _a, _b, _c, _d;

    return merge(((_a = defaultLocale.Form) === null || _a === void 0 ? void 0 : _a.defaultValidateMessages) || {}, ((_c = (_b = memoedConfig.locale) === null || _b === void 0 ? void 0 : _b.Form) === null || _c === void 0 ? void 0 : _c.defaultValidateMessages) || {}, ((_d = memoedConfig.form) === null || _d === void 0 ? void 0 : _d.validateMessages) || {}, (form === null || form === void 0 ? void 0 : form.validateMessages) || {});
  }, [memoedConfig, form === null || form === void 0 ? void 0 : form.validateMessages]);

  if (Object.keys(validateMessages).length > 0) {
    childNode = /*#__PURE__*/React.createElement(ValidateMessagesContext.Provider, {
      value: validateMessages
    }, children);
  }

  if (locale) {
    childNode = /*#__PURE__*/React.createElement(LocaleProvider, {
      locale: locale,
      _ANT_MARK__: ANT_MARK
    }, childNode);
  }

  if (iconPrefixCls || csp) {
    childNode = /*#__PURE__*/React.createElement(IconContext.Provider, {
      value: memoIconContextValue
    }, childNode);
  }

  if (componentSize) {
    childNode = /*#__PURE__*/React.createElement(SizeContextProvider, {
      size: componentSize
    }, childNode);
  } // =================================== Motion ===================================


  childNode = /*#__PURE__*/React.createElement(MotionWrapper, null, childNode); // ================================ Dynamic theme ================================

  var memoTheme = React.useMemo(function () {
    var _a = mergedTheme || {},
        algorithm = _a.algorithm,
        token = _a.token,
        components = _a.components,
        rest = __rest(_a, ["algorithm", "token", "components"]);

    var themeObj = algorithm && (!Array.isArray(algorithm) || algorithm.length > 0) ? createTheme(algorithm) : defaultTheme;
    var parsedComponents = {};
    Object.entries(components || {}).forEach(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
          componentName = _ref3[0],
          componentToken = _ref3[1];

      var parsedToken = Object.assign({}, componentToken);

      if ('algorithm' in parsedToken) {
        if (parsedToken.algorithm === true) {
          parsedToken.theme = themeObj;
        } else if (Array.isArray(parsedToken.algorithm) || typeof parsedToken.algorithm === 'function') {
          parsedToken.theme = createTheme(parsedToken.algorithm);
        }

        delete parsedToken.algorithm;
      }

      parsedComponents[componentName] = parsedToken;
    });
    return Object.assign(Object.assign({}, rest), {
      theme: themeObj,
      token: Object.assign(Object.assign({}, defaultSeedToken), token),
      components: parsedComponents
    });
  }, [mergedTheme]);

  if (theme) {
    childNode = /*#__PURE__*/React.createElement(DesignTokenContext.Provider, {
      value: memoTheme
    }, childNode);
  } // =================================== Render ===================================


  if (componentDisabled !== undefined) {
    childNode = /*#__PURE__*/React.createElement(DisabledContextProvider, {
      disabled: componentDisabled
    }, childNode);
  }

  return /*#__PURE__*/React.createElement(ConfigContext.Provider, {
    value: memoedConfig
  }, childNode);
};

var ConfigProvider = function ConfigProvider(props) {
  var context = React.useContext(ConfigContext);
  var antLocale = React.useContext(LocaleContext);
  return /*#__PURE__*/React.createElement(ProviderChildren, Object.assign({
    parentContext: context,
    legacyLocale: antLocale
  }, props));
};

ConfigProvider.ConfigContext = ConfigContext;
ConfigProvider.SizeContext = SizeContext;
ConfigProvider.config = setGlobalConfig;
ConfigProvider.useConfig = useConfig;
Object.defineProperty(ConfigProvider, 'SizeContext', {
  get: function get() {
    process.env.NODE_ENV !== "production" ? warning(false, 'ConfigProvider', 'ConfigProvider.SizeContext is deprecated. Please use `ConfigProvider.useConfig().componentSize` instead.') : void 0;
    return SizeContext;
  }
});

if (process.env.NODE_ENV !== 'production') {
  ConfigProvider.displayName = 'ConfigProvider';
}

export default ConfigProvider;